import React from 'react';
import { ChevronDown } from 'lucide-react';
import Badge from './Badge.tsx';

type IconComponent = React.ComponentType<{ className?: string }>;

interface FeeExplanationAccordionProps {
    icon: IconComponent;
    title: React.ReactNode;
    summary: React.ReactNode;
    details: React.ReactNode;
    isOpen: boolean;
    onToggle: () => void;
    applies: boolean | 'optional';
    factFind?: unknown;
}

const FeeExplanationAccordion = ({ icon: Icon, title, summary, details, isOpen, onToggle, applies }: FeeExplanationAccordionProps) => {
    const getStatus = () => {
        if (applies === 'optional') {
            return <Badge tone="info" dot>Optional</Badge>;
        }
        if (applies) {
            return <Badge tone="positive" dot>May Apply to You</Badge>;
        }
        return <Badge tone="neutral" dot>May Not Apply</Badge>;
    };

    return (
        <div className={`group bg-white rounded-2xl border-2 transition-all duration-300 overflow-hidden ${
            isOpen
                ? 'border-blue-300 shadow-xl shadow-blue-100/50'
                : 'border-gray-200 shadow-md hover:shadow-lg hover:border-blue-200'
        }`}>
            <button
                onClick={onToggle}
                className="w-full text-left p-6 md:p-8 flex items-start gap-5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-2xl"
            >
                <div className={`flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-300 ${
                    isOpen
                        ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30 scale-110'
                        : 'bg-gradient-to-br from-blue-100 to-blue-50 text-blue-600 group-hover:from-blue-200 group-hover:to-blue-100'
                }`}>
                    <Icon className={`w-7 h-7 transition-transform duration-300 ${isOpen ? 'scale-110' : ''}`} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                        <h3 className={`text-xl md:text-2xl font-bold transition-colors duration-300 ${
                            isOpen ? 'text-blue-700' : 'text-gray-800 group-hover:text-blue-600'
                        }`}>{title}</h3>
                        <div className="flex items-center gap-3 flex-shrink-0">
                            {getStatus()}
                            <ChevronDown className={`w-6 h-6 text-gray-400 transition-all duration-300 ${
                                isOpen ? 'rotate-180 text-blue-600' : 'group-hover:text-gray-600'
                            }`} />
                        </div>
                    </div>
                    <p className="text-base text-gray-600 leading-relaxed pr-4">{summary}</p>
                </div>
            </button>
            {isOpen && (
                <div className="px-6 md:px-8 pb-6 md:pb-8 pl-19 md:pl-24" style={{
                    animation: 'fadeIn 0.3s ease-in-out'
                }}>
                    <style>{`
                        @keyframes fadeIn {
                            from { opacity: 0; transform: translateY(-10px); }
                            to { opacity: 1; transform: translateY(0); }
                        }
                    `}</style>
                    <div className="pt-4 border-t border-gray-200">
                        {/* The page's permission ("open one only if you want the detail") travels with the content. */}
                        <p className="text-[11px] font-semibold tracking-wider text-slate-400 uppercase mb-3">The detail — optional</p>
                        <div className="prose prose-sm max-w-none text-gray-700">
                            {details}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FeeExplanationAccordion;
